import PropTypes from 'prop-types';
import { Link, NavLink } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import Logo from '../Logo/Logo';

function Menu({ questions, answers, questionIndex, questionsValidated }) {
    // On met en place l'état Ouvert / Fermé
    const [ isOpen, setIsOpen ] = useState(false);

    // On met en place l'évolution de l'overflow en fonction de l'état isOpen
    useEffect(() => {
        // On récupère le positionnement initiale du bouton vis à vis de la page
        const burgerButton = document.querySelector('.menu__button');
        const burgerButtonOffsetLeft = burgerButton?.offsetLeft || 0;
        burgerButton.style.setProperty('--burger-pos-x', `${burgerButtonOffsetLeft}px`);

        if (isOpen) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "";
        }

        return () => {
          document.body.style.overflow = "";
        };
      }, [isOpen]);

    // Mise en place de la fonction qui gère le changement d'état du menu au click sur le bouton
    const handleOpenMenu = () => setIsOpen(!isOpen);

    // On construit les entrées du menu
    const menuEntries = questions.map((q, i) => {
        const isAnswered = answers[`q${i}`] !== undefined;
        const isActual = questionIndex === i;
        let content;
        if(isAnswered) {
            content = <li key={'q' + i + 1} className="menu-item menu-item--is-answered">
                <NavLink
                    to={'/quiz/' + i}
                    onClick={handleOpenMenu}
                    aria-label={`${i + 1} sur ${questions.length}, ${q.dates.min} à ${q.dates.max}`}>
                    <span aria-hidden="true">{i + 1} / {questions.length}</span>
                    <p className="menu__dates">
                        <span>{q.dates.min}</span>
                        <span className="menu-separator menu-separator--horizontal" aria-label="à"></span>
                        <span>{q.dates.max}</span>
                    </p>
                </NavLink>
            </li>
        } else if(isActual) {
            content = <li key={'q' + i + 1} className="menu-item menu-item--is-actual">
                <div
                    aria-current="page"
                    className="current-page">
                    <span className="sr-only">{i + 1} sur {questions.length}, {q.dates.min} à {q.dates.max}</span>
                    <span aria-hidden="true">{i + 1} / {questions.length}</span>
                    <p aria-hidden="true" className="menu__dates">
                        <span>{q.dates.min}</span>
                        <span className="menu-separator menu-separator--horizontal" aria-label="à"></span>
                        <span>{q.dates.max}</span>
                    </p>
                </div>
            </li>
        } else {
            content = <li
                key={'q' + i + 1}
                className="menu-item menu-item--is-hidden">
                <span className="sr-only">{i + 1} sur {questions.length}, période masquée</span>
                <span aria-hidden="true">{i + 1} / {questions.length}</span>
                <p aria-hidden="true" className="menu__dates">
                    <span className="menu-separator menu-separator--date-emulator menu-separator--horizontal"></span>
                    <span className="menu-separator menu-separator--horizontal" aria-label="à"></span>
                    <span className="menu-separator menu-separator--date-emulator menu-separator--horizontal"></span>
                </p>
            </li>
        }
        return content
    })

    // Déclaration de la variable pour tester l'affichage du bouton amenant à la page de résultat
    const allQuestionsAnsweredAndValidated = Object.keys(answers).length === questions.length && questionsValidated === questions.length;

    // On configure les classes du conteneur interne
    const innerContainerClasses = (!isOpen) ? 'menu__innerContainer grid--container' : 'menu__innerContainer is-open grid--container';

    // Mise en place des références pour cibler le premier élément du menu et le dernier
    // Et de la fonction permettant de réaliser le piège au clavier
    const firstButton = useRef(null);
    const lastButton = useRef(null);
    const handleKeyDown = (e, pos) => {
        if(isOpen && pos === 'first' && e.keyCode === 9 && e.shiftKey === true && lastButton.current !== null) {
            e.preventDefault();
            lastButton.current.focus();
        }
        else if(isOpen && pos === 'last' && e.keyCode === 9 && e.shiftKey === false && firstButton.current !== null) {
            e.preventDefault();
            firstButton.current.focus();
        }
    }

    // Variation de la classe du bouton burger en fonction du statut du menu ainsi
    // que de son aria-label
    const burgerClasses = isOpen ? 'menu__button is-open' : 'menu__button';
    const burgerAriaLabel = isOpen ? 'fermer le menu' : 'ouvrir le menu';

    return (
        <header className='menu__outerContainer'>
            <button
                className={burgerClasses}
                aria-controls="menu-navigation"
                aria-expanded={isOpen}
                aria-label={burgerAriaLabel}
                ref={firstButton}
                onClick={handleOpenMenu}
                onKeyDown={(e) => handleKeyDown(e, 'first')}>
                    <span aria-hidden="true" className="menu__button-line menu__button-line--1"></span>
                    <span aria-hidden="true" className="menu__button-line menu__button-line--2"></span>
                    <span aria-hidden="true" className="menu__button-line menu__button-line--3"></span>
            </button>
            {isOpen &&
                <div id="menu-navigation" className={innerContainerClasses}>
                    {!isNaN(questionIndex) &&
                        <div className="quiz--progress" aria-label={`${questionIndex + 1} sur ${questions.length}`}>
                            <p aria-hidden="true">{questionIndex + 1} / {questions.length}</p>
                        </div>
                    }
                    <nav role="navigation" className="menu menu--primary">
                        <ul>
                            {menuEntries}
                            <li key={'qresult'} className="menu-item menu-item--result-page">
                                {allQuestionsAnsweredAndValidated ? (
                                    <NavLink to="/quiz/result" onClick={handleOpenMenu}>ma radio</NavLink>
                                ) : (
                                    'ma radio'
                                )}

                            </li>
                        </ul>
                    </nav>
                    <div className="menu menu--secondary desktop-col-start--1 desktop-col-end--15 mobile-col-start--1 mobile-col-end--7">
                        <ul className="menu menu--secondary__innerContainer desktop-col-start--2 desktop-col-end--14 mobile-col-start--2 mobile-col-end--6">
                            <li>
                                <NavLink to="/a-propos" onClick={handleOpenMenu}>à propos</NavLink>
                            </li>
                            <li>
                                <a className="link link--external" target="_blank" href="https://www.cite-sciences.fr/fr/outils/mentions-legales">mentions légales</a>
                            </li>
                            <li>
                                <a className="link link--external" target="_blank" href="https://www.cite-sciences.fr/fr/outils/accessibilite-du-site-internet/declaration-de-conformite">accessibilité</a>
                            </li>
                            <li>
                                <Link
                                    ref={lastButton}
                                    className="link link--external link--image menu__link--external"
                                    to="https://www.cite-sciences.fr/fr/"
                                    onKeyDown={(e) => handleKeyDown(e, 'last')}>
                                        <Logo size={100} className="menu__logo" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            }
        </header>
    )
}

Menu.propTypes = {
    questions: PropTypes.object.isRequired,
    questionIndex: PropTypes.number.isRequired,
    answers: PropTypes.object.isRequired,
    questionsValidated: PropTypes.number.isRequired,
}

export default Menu;
